import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { toast } from 'sonner';
import {
  ChartBar, House, Trophy, CircleNotch, CurrencyDollar, ArrowDown,
  FilePdf, FileXls, Medal,
} from '@phosphor-icons/react';

const fmtQ = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const Reportes = () => {
  const now = new Date();
  const [filters, setFilters] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    period: 'monthly',
    branch: '',
  });
  const [data, setData] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('vendor'); // vendor | branch
  const [exporting, setExporting] = useState({ pdf: false, xls: false });

  const buildParams = () => {
    const p = new URLSearchParams({
      year: filters.year, month: filters.month, period: filters.period,
    });
    if (filters.branch) p.set('branch', filters.branch);
    return p;
  };

  const exportFile = async (kind) => {
    setExporting((prev) => ({ ...prev, [kind]: true }));
    try {
      const url = kind === 'pdf'
        ? `/work-plan/reports/sales/export-pdf?${buildParams().toString()}`
        : `/work-plan/reports/sales/export-excel?${buildParams().toString()}`;
      const res = await api.get(url, { responseType: 'blob' });
      const mime = kind === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: mime });
      const u = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u;
      a.download = `reporte-ventas-${filters.period}-${filters.year}-${filters.month}.${kind === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(u);
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(`Error exportando ${kind.toUpperCase()}`);
    } finally {
      setExporting((prev) => ({ ...prev, [kind]: false }));
    }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        year: filters.year, month: filters.month, period: filters.period,
      });
      if (filters.branch) params.set('branch', filters.branch);
      const [r, b] = await Promise.all([
        api.get(`/work-plan/reports/sales?${params.toString()}`),
        api.get('/work-plan/branches'),
      ]);
      setData(r.data);
      setBranches(b.data.branches || []);
    } catch (e) {
      toast.error('Error cargando reportes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [filters.year, filters.month, filters.period, filters.branch]);

  const totals = data?.totals || {};
  const periodLabel = {
    monthly: `${new Date(filters.year, filters.month - 1).toLocaleDateString('es-GT', { month: 'long' })} ${filters.year}`,
    quarterly: `Q${Math.ceil(filters.month / 3)} ${filters.year}`,
    semester: `${filters.month <= 6 ? 'S1' : 'S2'} ${filters.year}`,
    annual: `Año ${filters.year}`,
  }[filters.period];

  return (
    <div className="p-6 max-w-[1400px] mx-auto" style={{ background: 'var(--bg)', color: 'var(--text)' }} data-testid="reportes-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--text-faint)] font-bold flex items-center gap-1.5">
            <ChartBar size={11} weight="fill" /> Reportes Ventas CYH + SA
          </p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight heading-font">Reporte de Ventas</h1>
          <p className="text-xs text-[var(--text-muted)] mt-1 capitalize">{periodLabel}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <select value={filters.period} onChange={(e) => setFilters({ ...filters, period: e.target.value })}
            className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm" data-testid="filter-period">
            <option value="monthly">Mensual</option>
            <option value="quarterly">Trimestral</option>
            <option value="semester">Semestral</option>
            <option value="annual">Anual</option>
          </select>
          {filters.period !== 'annual' && (
            <select value={filters.month} onChange={(e) => setFilters({ ...filters, month: Number(e.target.value) })}
              className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm" data-testid="filter-month">
              {Array.from({ length: 12 }, (_, i) => i + 1).map(m =>
                <option key={m} value={m}>{new Date(2000, m - 1).toLocaleDateString('es-GT', { month: 'long' })}</option>)}
            </select>
          )}
          <select value={filters.year} onChange={(e) => setFilters({ ...filters, year: Number(e.target.value) })}
            className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm" data-testid="filter-year">
            {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={filters.branch} onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
            className="bg-[var(--surface)] border border-[var(--border)] text-[var(--text)] rounded-md px-3 py-2 text-sm" data-testid="filter-branch">
            <option value="">Todas las sucursales</option>
            {branches.map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          <button
            onClick={() => exportFile('pdf')}
            disabled={exporting.pdf}
            className="inline-flex items-center gap-1.5 bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-50"
            data-testid="btn-export-pdf"
          >
            {exporting.pdf ? <CircleNotch size={14} className="animate-spin" /> : <FilePdf size={14} weight="bold" />}
            PDF
          </button>
          <button
            onClick={() => exportFile('xls')}
            disabled={exporting.xls}
            className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-2 rounded-md disabled:opacity-50"
            data-testid="btn-export-excel"
          >
            {exporting.xls ? <CircleNotch size={14} className="animate-spin" /> : <FileXls size={14} weight="bold" />}
            Excel
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="text-center py-16 text-[var(--text-muted)]"><CircleNotch size={28} className="animate-spin inline mb-2" /><p className="text-sm">Cargando...</p></div>
      ) : (
        <>
          {/* TOTALS */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <BigCard label="Meta total" value={fmtQ(totals.target_total)} color="slate" sub={`CYH ${fmtQ(totals.target_cyh)} · SA ${fmtQ(totals.target_sa)}`} />
            <BigCard label="Ventas total" value={fmtQ(totals.sales_total)} color="emerald" sub={`CYH ${fmtQ(totals.sales_cyh)} · SA ${fmtQ(totals.sales_sa)}`} />
            <BigCard label="% Avance total" value={totals.percent_total === null ? '—' : `${totals.percent_total}%`} color={pctColor(totals.percent_total)} />
            <BigCard label="Falta" value={fmtQ(Math.max(0, (totals.target_total || 0) - (totals.sales_total || 0)))} color="amber" />
          </div>

          {/* TOP VENDORS RANKING */}
          {data.by_vendor.filter(v => v.percent_total !== null).length > 0 && (
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-5 mb-5 shadow-[var(--shadow-sm)]" data-testid="top-vendors-card">
              <div className="flex items-center gap-2 mb-3">
                <Medal size={20} weight="fill" className="text-amber-500" />
                <h3 className="text-sm font-black heading-font uppercase tracking-wide">Ranking de Vendedores Efectivos</h3>
                <span className="ml-auto text-[10px] text-[var(--text-faint)] font-bold">Ordenado por % cumplimiento total</span>
              </div>
              <div className="space-y-1.5">
                {data.by_vendor.filter(v => v.percent_total !== null).slice(0, 10).map((v, i) => {
                  const pct = v.percent_total;
                  const color = pct >= 100 ? 'bg-emerald-500' : pct >= 70 ? 'bg-blue-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500';
                  const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
                  return (
                    <div key={v.user_id} className="flex items-center gap-3 py-1.5 border-b border-[var(--border)] last:border-b-0" data-testid={`rank-${i + 1}`}>
                      <span className="text-base font-black w-8 text-center shrink-0">{medal}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <p className="text-sm font-black text-[var(--text)] truncate">{v.user_name}</p>
                          <span className={`text-sm font-black ${pctColorClass(pct)}`}>{pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--bg)] overflow-hidden">
                          <div className={`h-full ${color} transition-all duration-500`} style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <p className="text-[10px] text-[var(--text-muted)] mt-1">
                          {v.branch || 'Sin sucursal'} · Vendido Q {Number(v.sales_total).toLocaleString('es-GT')} de Q {Number(v.target_total).toLocaleString('es-GT')}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* View toggle */}
          <div className="flex gap-1 bg-[var(--surface)] border border-[var(--border)] rounded-md p-0.5 mb-4 w-fit">
            <button onClick={() => setView('vendor')} className={`px-3 py-1.5 text-xs font-bold rounded ${view === 'vendor' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)]'}`} data-testid="view-vendor">Por Vendedor</button>
            <button onClick={() => setView('branch')} className={`px-3 py-1.5 text-xs font-bold rounded ${view === 'branch' ? 'bg-[var(--primary)] text-white' : 'text-[var(--text-muted)]'}`} data-testid="view-branch">Por Sucursal</button>
          </div>

          {view === 'vendor' ? (
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] overflow-x-auto shadow-[var(--shadow-sm)]" data-testid="vendor-table">
              <table className="w-full text-xs min-w-[800px]">
                <thead className="bg-[var(--surface-muted)] text-[10px] uppercase tracking-wide font-bold text-[var(--text-faint)]">
                  <tr>
                    <th className="text-left px-3 py-2">Vendedor</th>
                    <th className="text-left px-2 py-2">Sucursal</th>
                    <th className="text-right px-2 py-2">Meta CYH</th>
                    <th className="text-right px-2 py-2">Venta CYH</th>
                    <th className="text-right px-2 py-2">% CYH</th>
                    <th className="text-right px-2 py-2">Meta SA</th>
                    <th className="text-right px-2 py-2">Venta SA</th>
                    <th className="text-right px-2 py-2">% SA</th>
                    <th className="text-right px-3 py-2 border-l border-[var(--border)]">% TOTAL</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_vendor.map((v) => (
                    <tr key={v.user_id} className="border-t border-[var(--border)] hover:bg-[var(--surface-muted)]" data-testid={`row-vendor-${v.user_id}`}>
                      <td className="px-3 py-1.5 font-black text-[var(--text)]">{v.user_name}</td>
                      <td className="px-2 py-1.5 text-[var(--text-muted)]">{v.branch || '—'}</td>
                      <td className="px-2 py-1.5 text-right">{fmtQ(v.target_cyh)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtQ(v.sales_cyh)}</td>
                      <td className={`px-2 py-1.5 text-right font-black ${pctColorClass(v.percent_cyh)}`}>{v.percent_cyh === null ? '—' : `${v.percent_cyh}%`}</td>
                      <td className="px-2 py-1.5 text-right">{fmtQ(v.target_sa)}</td>
                      <td className="px-2 py-1.5 text-right">{fmtQ(v.sales_sa)}</td>
                      <td className={`px-2 py-1.5 text-right font-black ${pctColorClass(v.percent_sa)}`}>{v.percent_sa === null ? '—' : `${v.percent_sa}%`}</td>
                      <td className={`px-3 py-1.5 text-right font-black border-l border-[var(--border)] ${pctColorClass(v.percent_total)}`}>{v.percent_total === null ? '—' : `${v.percent_total}%`}</td>
                    </tr>
                  ))}
                  {data.by_vendor.length === 0 && (
                    <tr><td colSpan={9} className="text-center py-8 text-[var(--text-muted)] text-sm">Sin datos para el periodo</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="branch-grid">
              {data.by_branch.map((b) => (
                <div key={b.branch} className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4 shadow-[var(--shadow-sm)]" data-testid={`branch-card-${b.branch}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <House size={18} weight="bold" className="text-[var(--primary)]" />
                      <h3 className="text-base font-black heading-font text-[var(--text)]">{b.branch}</h3>
                    </div>
                    <span className={`text-lg font-black ${pctColorClass(b.percent_total)}`}>{b.percent_total === null ? '—' : `${b.percent_total}%`}</span>
                  </div>
                  <p className="text-[10px] text-[var(--text-faint)] uppercase font-bold mb-2">{b.vendors} vendedor{b.vendors === 1 ? '' : 'es'}</p>
                  <div className="space-y-2 text-xs">
                    <div className="grid grid-cols-3 gap-2">
                      <div><p className="text-[10px] text-[var(--text-muted)] uppercase font-bold">CYH</p><p className="font-bold text-[var(--text)]">{fmtQ(b.sales_cyh)} <span className="text-[10px] text-[var(--text-faint)]">/ {fmtQ(b.target_cyh)}</span></p><p className={`text-[10px] font-black ${pctColorClass(b.percent_cyh)}`}>{b.percent_cyh === null ? '—' : `${b.percent_cyh}%`}</p></div>
                      <div><p className="text-[10px] text-[var(--text-muted)] uppercase font-bold">SA</p><p className="font-bold text-[var(--text)]">{fmtQ(b.sales_sa)} <span className="text-[10px] text-[var(--text-faint)]">/ {fmtQ(b.target_sa)}</span></p><p className={`text-[10px] font-black ${pctColorClass(b.percent_sa)}`}>{b.percent_sa === null ? '—' : `${b.percent_sa}%`}</p></div>
                      <div><p className="text-[10px] text-[var(--text-muted)] uppercase font-bold">Total</p><p className="font-black text-[var(--text)]">{fmtQ(b.sales_total)} <span className="text-[10px] text-[var(--text-faint)]">/ {fmtQ(b.target_total)}</span></p></div>
                    </div>
                  </div>
                </div>
              ))}
              {data.by_branch.length === 0 && (
                <p className="col-span-2 text-center py-8 text-[var(--text-muted)] text-sm">Sin sucursales en el periodo</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const pctColor = (pct) => pct === null ? 'slate' : pct >= 100 ? 'emerald' : pct >= 70 ? 'blue' : pct >= 40 ? 'amber' : 'red';
const pctColorClass = (pct) => pct === null ? 'text-[var(--text-faint)]'
  : pct >= 100 ? 'text-emerald-600 dark:text-emerald-400'
  : pct >= 70 ? 'text-blue-600 dark:text-blue-400'
  : pct >= 40 ? 'text-amber-600 dark:text-amber-400'
  : 'text-red-600 dark:text-red-400';

const BigCard = ({ label, value, color, sub }) => {
  const map = {
    slate: 'border-[var(--border)] bg-[var(--surface)] text-[var(--text)]',
    emerald: 'border-emerald-500/30 bg-emerald-500/5 text-emerald-600 dark:text-emerald-400',
    blue: 'border-blue-500/30 bg-blue-500/5 text-blue-600 dark:text-blue-400',
    amber: 'border-amber-500/30 bg-amber-500/5 text-amber-600 dark:text-amber-400',
    red: 'border-red-500/30 bg-red-500/5 text-red-600 dark:text-red-400',
  };
  return (
    <div className={`border rounded-[14px] p-4 ${map[color] || map.slate}`}>
      <p className="text-[10px] uppercase tracking-wide font-bold opacity-80">{label}</p>
      <p className="text-2xl font-black text-[var(--text)]">{value}</p>
      {sub && <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{sub}</p>}
    </div>
  );
};

export default Reportes;
